import React, { FC, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Container,
  ImageList,
  ImageListItem,
  Typography,
} from "@mui/material";
import FlightOutlinedIcon from "@mui/icons-material/FlightOutlined";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import moment from "moment";
import "moment/locale/fr";
import DOMPurify from "dompurify";

import countryService from "services/countryService";
import Country from "models/Country";
import { RootState } from "store";
import classes from "./style";
import ImagesCarousel from "../carousel/Carousel";
import { getImageUrl } from "utils/imageUtils";
import { safeSlice } from "utils/array";
import { toQueryParams } from "helpers/queryHelpers";
import proUserService from "services/proUserService";
import HasProUser from "models/ProUser";

moment.locale("fr");

type ImgItem = {
  img: string;
  title: string;
  cols?: number;
  rows?: number;
};

interface LocationState {
  searchCriteria?: {
    from: Date | string;
    to: Date | string;
    stayTypes: Array<{ id: number; name?: string }>;
  };
  [key: string]: any;
}

const sanitize = (html?: string) => ({ __html: DOMPurify.sanitize(html ?? "") });

const Destination: FC<{ propsId?: number }> = ({ propsId }) => {
  const { id } = useParams();
  const countryId = id ? Number(id) : propsId;

  const navigate = useNavigate();
  const location = useLocation() as { state?: LocationState };

  const isAuthenticated = useSelector<RootState, boolean>(
    (state) => state.globalSlice.isAuthenticated
  );

  const [destination, setDestination] = useState<Country | undefined>();
  const [proUser, setProUser] = useState<HasProUser | undefined>();
  const [itemData, setItemData] = useState<ImgItem[]>([]);
  const [openCarousel, setOpenCarousel] = useState(false);
  const [selectedImage, setSelectedImage] = useState<ImgItem | null>(null);

  const srcset = useMemo(
    () => (image: string, size: number, rows = 1, cols = 1) => ({
      src: `${image}`,
      srcSet: `${image}`,
    }),
    []
  );

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      if (!countryId || Number.isNaN(countryId)) return;
      try {
        const res = await countryService.getFullBriefDisplay(countryId);
        if (!mounted) return;
        setDestination(res?.[0]);
      } catch {
        setDestination(undefined);
      }
    };
    load();
    return () => {
      mounted = false;
    };
  }, [countryId]);

  useEffect(() => {
    const load = async () => {
      if (!countryId || Number.isNaN(countryId) || !isAuthenticated) return;
      try {
        const res = await proUserService.hasPro(countryId);
        setProUser(res);
      } catch {
        setProUser(undefined);
      }
    };
    load();
  }, [countryId, isAuthenticated]);

  useEffect(() => {
    if (!destination) {
      setItemData([]);
      return;
    }
    const pics = destination.brief?.destinationBriefPictures ?? [];
    if (pics.length === 0) {
      setItemData([]);
      return;
    }

    const w = Math.min(3, Math.max(1, pics.length > 4 ? 3 : pics.length > 2 ? 2 : 1));

    const mapped: ImgItem[] = pics.map((p, idx) => ({
      img: getImageUrl(p.url),
      title: destination.name,
      cols: (idx % w) || 1,
      rows: Math.floor(idx / w) || 1,
    }));

    setItemData(mapped);
  }, [destination]);

  useEffect(() => {
    if (selectedImage) setOpenCarousel(true);
  }, [selectedImage]);

  const chatWithPro = async () => {
    if (!proUser || !proUser.hasPro) return;
    try {
      await proUserService.chat(proUser.pro.id);
      goMessages();
    } catch (e) {
      setProUser(undefined);
    }
  }

  const goMessages = () => {
    navigate('/messagerie');
  }

  const handleCTA = () => {
    if (!destination) return;

    const sc = location.state?.searchCriteria;
    if (sc) {
      const query = toQueryParams({
        destinationId: destination.id,
        stayTypes: (sc.stayTypes ?? []).map((k) => k.id),
        from: moment(new Date(sc.from)).format("YYYY-MM-DD HH:mm:ss"),
        to: moment(new Date(sc.to)).format("YYYY-MM-DD HH:mm:ss"),
      });
      navigate("/liste-profils?" + query);
      return;
    }

    localStorage.setItem(
      "SEARCH",
      JSON.stringify({
        destinations: [destination],
        durations: [],
        stayTypes: [],
        budgets: [],
        transports: [],
        toPays: false,
        toProfils: false,
      })
    );
    navigate("/");
  };

  if (!countryId) {
    return (
      <Container sx={{ py: 6 }}>
        <Typography variant="h6" gutterBottom>
          Destination inconnue
        </Typography>
        <Typography variant="body2" color="text.secondary">
          L’identifiant de destination est manquant ou invalide.
        </Typography>
      </Container>
    );
  }

  if (!destination) {
    return (
      <Container sx={{ py: 6 }}>
        <Typography variant="h6">Chargement…</Typography>
      </Container>
    );
  }

  const main = itemData[0];
  const second = itemData[1];

  return (
    <>
      <Container sx={[classes.root, classes.calendrier, classes.destinationBloc, classes.fichePays]}>
        <Typography variant="h1">{destination.name}</Typography>

        <Box sx={classes.destinationContent}>
          {destination?.brief?.title && (
            <Typography variant="h2">{destination.brief.title}</Typography>
          )}

          {main && (
            <img
              width="80%"
              {...srcset(main.img, 50, 1, 1)}
              alt={main.title}
              loading="lazy"
              onClick={() => setSelectedImage(main)}
              style={{ cursor: "zoom-in" }}
            />
          )}

          {destination?.brief?.description && (
            <Typography variant="body1" sx={{ mt: 2 }}>
              {destination.brief.description}
            </Typography>
          )}

          {second && (
            <img
              width="80%"
              {...srcset(second.img, 50, 1, 1)}
              alt={second.title}
              loading="lazy"
              onClick={() => setSelectedImage(second)}
              style={{ cursor: "zoom-in", marginTop: 16 }}
            />
          )}

          {destination?.brief?.content && (
            <Box sx={{ mt: 2 }}>
              <div dangerouslySetInnerHTML={sanitize(destination.brief.content)} />
            </Box>
          )}

          {itemData.length > 2 && (
            <ImageList variant="quilted" cols={4} sx={{ mt: 2 }}>
              {safeSlice(itemData, 2).map((item) => (
                <ImageListItem
                  key={item.img}
                  cols={item.cols || 1}
                  rows={item.rows || 1}
                  onClick={() => setSelectedImage(item)}
                  sx={{ cursor: "zoom-in" }}
                >
                  <img {...srcset(item.img, 121, 1, item.cols || 1)} alt={item.title} loading="lazy" />
                </ImageListItem>
              ))}
            </ImageList>
          )}

          {id && (
            <Button variant="contained" size="small" onClick={handleCTA} sx={{ my: 2 }}>
              Trouver quelqu'un
              <FlightOutlinedIcon sx={{ ml: 1 }} />
            </Button>
          )}
          {proUser && proUser.hasPro && (
            <Button variant="contained" size="small" onClick={chatWithPro} sx={{ my: 2 }}>
              {proUser.pro.description}
              <FlightOutlinedIcon sx={{ ml: 1 }} />
            </Button>
          )}
        </Box>
      </Container>

      {itemData.length > 0 && (
        <ImagesCarousel
          open={openCarousel}
          images={itemData}
          selectedImage={selectedImage}
          handleClose={() => {
            setOpenCarousel(false);
            setSelectedImage(null);
          }}
        />
      )}
    </>
  );
};

export default Destination;
